import React, {FC, useContext, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';

import IconButton from '@mui/material/IconButton';
import {Box, Tooltip, Typography} from '@mui/material';

import DownloadSvgIcon from 'assets/icons/buttons/DownloadSvgIcon';

import {PublicKeyTypes} from 'appRedux/actions/crypto/types';
import {RootReducer} from 'appRedux/reducers';
import {ExportCryptoKeysTypes} from 'appRedux/actions/auth/types';

import {AlertContext} from 'contexts/alert/context';
import {CryptoContext} from 'contexts/crypto/context';

import ModalWrapper from 'components/ModalWrapper/ModalWrapper';
import CryptoPasswordForm from 'components/PublicKeysList/CryptoPasswordForm';

import {
    encryptStringWithPasswordKey,
    exportPrivateKey,
    exportPublicKey,
    getPasswordKeyAndIv,
} from 'helpers/cryptoApiHelper';

import {ALERT_TYPE_ERROR} from 'config/index';

interface ExportKeyButtonType {
    item: PublicKeyTypes;
}

const ExportKeyButton: FC<ExportKeyButtonType> = () => {
    const [t] = useTranslation();

    const {showAlert} = useContext(AlertContext);

    const [isOpened, setIsOpened] = useState<boolean>(false);

    const toggleIsOpened = () => {
        setIsOpened(previous => !previous);
    };

    const {keys} = useContext(CryptoContext);

    const {
        profile: {profile},
        crypto: {currentUserPublicKeys},
    } = useSelector<RootReducer>((state: RootReducer) => state) as RootReducer;

    const handleExport = async (values: ExportCryptoKeysTypes) => {
        if (!keys) return;

        try {
            const publicKey = await exportPublicKey(keys.publicKey);
            const privateKey = await exportPrivateKey(keys.privateKey);

            const keysToExport = {
                name: keys.name,
                publicKey,
                privateKey,
                uuid: keys.uuid,
            };
            const keysString = JSON.stringify(keysToExport);

            const {key, iv} = await getPasswordKeyAndIv(values.keyPassword);
            const ecnrypted = await encryptStringWithPasswordKey(keysString, key, iv);

            const saveFile = (function () {
                const a = document.createElement('a');
                return function (data: string, fileName: string) {
                    const blob = new Blob([data], {type: 'octet/stream'});
                    const url = window.URL.createObjectURL(blob);
                    a.href = url;
                    a.download = fileName;
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();
                };
            })();

            const keyname = currentUserPublicKeys?.find(key => key.data === publicKey)?.title || 'key';
            const domain = location.host.split('.')[0];

            saveFile(ecnrypted, `${domain}_${profile?.email.split('@')[0]}_${keyname}.crypto`);
            toggleIsOpened();
        } catch (e) {
            showAlert && showAlert(ALERT_TYPE_ERROR);
        }
    };

    return (
        <>
            <Tooltip title={t('common.publicKeys.exportCryptoKeysLabel')} placement={'top'} arrow disableInteractive>
                <IconButton onClick={toggleIsOpened}>
                    <DownloadSvgIcon />
                </IconButton>
            </Tooltip>
            <ModalWrapper
                title={t('common.publicKeys.exportCryptoKeysLabel')}
                isShowModal={isOpened}
                toggleModal={toggleIsOpened}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'start',
                        py: 2,
                        width: '100%',
                    }}
                >
                    <Typography align="center" variant="body2" sx={{mt: 2}}>
                        {t('common.publicKeys.exportCryptoKeysDescription')}
                    </Typography>
                    <CryptoPasswordForm handleSubmit={handleExport} />
                </Box>
            </ModalWrapper>
        </>
    );
};

export default ExportKeyButton;
